import { useState } from "react";
import {
  RunContext,
  useAgentRunManager,
} from "../../components/agent/hooks/WebsocketHook";

const defaultContext = {
  messageId: "msg-123456",
  clientId: "4b1088ab-7c10-4a98-b1cf-6749f777c700",
  companyId: "295ebaca-abc6-46a1-9bd6-82140236c0ed",
  agentId: "7fc53806-8031-49d9-9f4b-c14b00cac200",
  baseUrl: "vaers-server",
  repositoryId: "",
};

export default function ContainerConnectionForm({ url }: { url: string }) {
  const { isConnected, isConnecting, runs, startRun, connect, disconnect } =
    useAgentRunManager({
      clientId: `client-${Math.random().toString(36).substring(2, 9)}`,
      onOpen: () => console.log("Connected to server"),
      onClose: () => console.log("Disconnected from server"),
      onError: (error) => console.error("WebSocket error:", error),
    });

  const [input, setInput] = useState("");
  const [agentType, setAgentType] = useState<"joke" | "story" | "dev">("joke");
  const [runContext, setRunContext] = useState<RunContext>(defaultContext);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim()) {
      startRun({
        agentType,
        input,
        context: runContext,
        url: "ws://localhost:3004/ws",
      });
      setInput("");
    }
  };

  const handleUpdateRunContext = (value: string, param: keyof RunContext) => {
    console.log("handleUpdateRunContext", value, param);
    setRunContext((prev) =>
      prev
        ? {
            ...prev,
            [param]: value,
          }
        : runContext,
    );
  };

  const handleConnectionToggle = () => {
    if (isConnected || isConnecting) {
      disconnect();
    } else {
      connect(url);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <div
              className={`mr-2 h-3 w-3 rounded-full ${
                isConnected
                  ? "bg-green-500"
                  : isConnecting
                    ? "bg-yellow-500"
                    : "bg-red-500"
              }`}
            />
            <span>
              {isConnected
                ? "Connected"
                : isConnecting
                  ? "Connecting..."
                  : "Disconnected"}
            </span>
          </div>

          <button
            onClick={handleConnectionToggle}
            className={`rounded px-4 py-2 text-white ${
              isConnected || isConnecting ? "bg-red-500" : "bg-green-500"
            }`}
          >
            {isConnected || isConnecting ? "Disconnect" : "Connect"}
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col space-y-2">
          <div className="flex flex-col space-y-2">
            <input
              type="text"
              value={runContext?.messageId || ""}
              onChange={(e) =>
                handleUpdateRunContext(e.target.value, "messageId")
              }
              placeholder="Message ID"
              className="flex-1 rounded border p-2"
              disabled={!isConnected}
            />

            <input
              type="text"
              value={runContext?.clientId || ""}
              onChange={(e) =>
                handleUpdateRunContext(e.target.value, "clientId")
              }
              placeholder="Client ID"
              className="flex-1 rounded border p-2"
              disabled={!isConnected}
            />

            <input
              type="text"
              value={runContext?.agentId || ""}
              onChange={(e) =>
                handleUpdateRunContext(e.target.value, "agentId")
              }
              placeholder="Agent ID"
              className="flex-1 rounded border p-2"
              disabled={!isConnected}
            />

            <input
              type="text"
              value={runContext?.companyId || ""}
              onChange={(e) =>
                handleUpdateRunContext(e.target.value, "companyId")
              }
              placeholder="Company ID"
              className="flex-1 rounded border p-2"
              disabled={!isConnected}
            />

            <input
              type="text"
              value={runContext?.baseUrl || ""}
              onChange={(e) =>
                handleUpdateRunContext(e.target.value, "baseUrl")
              }
              placeholder="Base URL"
              className="flex-1 rounded border p-2"
              disabled={!isConnected}
            />

            <input
              type="text"
              value={runContext?.repositoryId || ""}
              onChange={(e) =>
                handleUpdateRunContext(e.target.value, "repositoryId")
              }
              placeholder="Repository ID"
              className="flex-1 rounded border p-2"
              disabled={!isConnected}
            />
          </div>
          <div className="flex space-x-2">
            <select
              value={agentType}
              onChange={(e) =>
                setAgentType(e.target.value as "joke" | "story" | "dev")
              }
              className="rounded border p-2"
            >
              <option value="joke">Joke Agent</option>
              <option value="story">Story Agent</option>
              <option value="dev">Dev Agent</option>
            </select>

            <div className="flex w-full flex-col space-y-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter your query..."
                className="flex-1 rounded border p-2"
                disabled={!isConnected}
              />

              <button
                type="submit"
                disabled={!isConnected || !input.trim()}
                className="rounded bg-blue-500 px-4 py-2 text-white disabled:bg-gray-300"
              >
                Send
              </button>
            </div>
          </div>
        </form>
        <div className="space-y-4">
          {Object.entries(runs).map(([runId, run]) => (
            <div key={runId} className="rounded border p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-bold">{runId}</div>
                <div
                  className={`rounded px-2 py-1 text-xs ${
                    run.status === "running"
                      ? "bg-blue-100 text-blue-800"
                      : run.status === "completed"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                  }`}
                >
                  {run.status}
                </div>
              </div>

              {run.error ? (
                <div className="mb-2 text-red-500">{run.error}</div>
              ) : null}

              <div className="whitespace-pre-wrap rounded bg-gray-50 p-3">
                {run.content || "Waiting for response..."}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
